import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { readRuntimeConfig, resolveSentryConfig } from './runtime-config';

/**
 * Initialize Sentry error tracking + Session Replay for the web frontend.
 * Call this as early as possible, before React renders.
 *
 * Fail-soft contract: with no DSN from either the container (`/config.js`) or
 * the build (VITE_SENTRY_DSN) this is a no-op, as in local dev and CI.
 *
 * The DSN and environment come from the container at runtime so one image can
 * serve dev and prod (see ./runtime-config.ts). That gives up one thing the
 * previous build-time-only read allowed: Rollup can no longer strip Sentry
 * (~80 kB gzip) out of a build with no DSN, because the DSN may still arrive at
 * runtime. Every deployed build already shipped Sentry, so only local builds
 * grow.
 */
export function initSentry() {
  const { dsn, environment, release } = resolveSentryConfig(readRuntimeConfig(), {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_APP_VERSION,
    mode: import.meta.env.MODE,
  });
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release,

    integrations: [
      // Replace the generic browserTracingIntegration with the react-router v6
      // variant so transactions carry real route names (e.g. /flows/:id) instead
      // of the unhelpful <unknown>. The app uses BrowserRouter (not a data router)
      // so we pass the react-router hooks directly here and wrap <Routes> in
      // App.tsx with withSentryReactRouterV6Routing.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Mask text and block media in replays by default so we never capture
      // PII from the DOM.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],

    // Keep tracing light; errors are the priority.
    tracesSampleRate: 0.1,

    // Record a replay only for sessions that hit an error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    sendDefaultPii: false,

    // Benign browser noise that isn't worth an alert.
    ignoreErrors: [
      /extensions\//i,
      /^chrome:\/\//i,
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      'AbortError',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });

  // eslint-disable-next-line no-console
  console.log(`[Sentry] Initialized (env=${environment}, release=${release})`);
}

/** Set the current user context for error tracking. Call this after login. */
export function setSentryUser(user: { id: string | number; email?: string } | null) {
  Sentry.setUser(user ? { id: String(user.id), email: user.email } : null);
}

/** Manually capture an exception with optional extra context. */
export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, { extra: context });
}

/** Sentry's React error boundary — wrap the app with this. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * HOC that instruments a <Routes> component for Sentry tracing.
 * Apply this once, around the top-level <Routes> in App.tsx.
 *
 * Usage:
 *   import { Routes } from 'react-router-dom';
 *   import { withSentryRouting } from '@/lib/sentry';
 *   const SentryRoutes = withSentryRouting(Routes);
 */
export const withSentryRouting = Sentry.withSentryReactRouterV6Routing;
